import TransferStepper from "components/transfers/transferStepper";
import ViewTable from "components/ViewTable";
import { TYPE_GET_RAW, useFetcherSelector } from "mrf/redux/ReduxHelper";
import dateTimeFormatter from "mrf/utils/DateTimeFormatter";
import useMRFTranslation from "mrf/utils/useMRFTranslation";
import AccordionTable from "mrf/view/AccordionTable";
import { Col, Grid } from "mrf/view/Grid";
import { ObjectTable } from "mrf/view/ObjectTable";
import Paper from "mrf/view/Paper";
import TabContainer from "mrf/view/TabContainer";
import PageTitle from "mrf/view/Title";

export default function CommunicationProcessView({ match }) {
    const t = useMRFTranslation();

    const { id } = match.params

    const process = useFetcherSelector({
        selector: state => state?.resources?.communicationProcesses,
        type: TYPE_GET_RAW,
        resource: 'communicationProcesses',
        endpoint: `/api/communicationProcesses/view/${id}`,
        unflatten: true,
        condition: () => true,
    }) || {};

    const processFields = [
        { name: t('Code'), key: 'id' },
        { name: t('Importer'), keys: ['importingInstitution', 'name'] },
        { name: t('Exporter'), keys: ['exportingInstitution', 'name'] },
        { name: t('Created at'), key: 'recordTimestamp', map: dateTimeFormatter },
        { name: t('Last update'), key: 'updatedTimestamp', map: dateTimeFormatter },
        { name: t('Current Status'), key: 'currentProcessStatus', map: t },
    ];

    const licensesFields = [
        { name: 'Code', key: 'licenseCode'},
        { name: 'License Type', key: 'licenseType', map:t },
        { name: 'Importer', keys: ['importingInstitution', 'name'] },
        { name: 'Exporter', keys: ['exportingInstitution', 'name'] },
        { name: 'Issuer', keys: ['issuerInstitution', 'name'] },
        { name: 'Issue Date', key: 'issueDate', map: dateTimeFormatter },
        { name: 'Expiration Date', key: 'expirationDate', map: dateTimeFormatter },
        { name: 'Data Removal Date', key: 'dataRemovalTimestamp', map: dateTimeFormatter }
    ];

    const licenseContentFields = [
        { name: 'Content Type', key: 'contentType', map: t},
        { name: 'Type', key: 'type', map: t},
        { name: 'Model', key: 'model', map: t},
        { name: 'Manufacturer', key: 'manufacturer', map: t},
        { name: 'Caliber', key: 'caliber', map: t},
        { name: 'Quantity', key: 'quantity'}
    ]

    const transitInstitutionFields = [
        { name: 'Code', key: 'code', map: t},
        { name: 'Name', key: 'name', map: t},
        { name: 'Invited By', key: 'invitedBy', map: t},
        { name: 'Joined At', key: 'joinedAt', map: dateTimeFormatter},
    ]
    
    const eventsFields = [
        { name: 'Type', key: 'type', map: t},
        { name: 'User', key: 'user', map: t},
        { name: 'Institution', key: 'institution', map: t},
        { name: 'Record Timestamp', key: 'recordTimestamp', map: dateTimeFormatter},
    ]

    const statusHistoryFields = [
        { name: 'Status', keys: ['status', 'name'], map: t},
        { name: 'Record Timestamp', key: 'recordTimestamp', map: dateTimeFormatter},
    ]
    
    const assignmentStatus = ['Open', 'Done', 'Canceled', 'Auto Closed'];

    const assignmentFields = [
        { name: t('Institution'), key: 'institution' },
        { name: t('Required Action'), key: 'requiredAction', map:t },
        { name: t('Created At'), key: 'createdAt', map: dateTimeFormatter },
        { name: t('Expected At'), key: 'expectedAt', map: dateTimeFormatter },
        { name: t('Last Update'), key: 'updatedAt', map: dateTimeFormatter },
        { name: t('Status'), key: 'status', map: (id) => t(assignmentStatus[id]) },
    ];

    const attachmentFields = [
        { name: t('Institution'), keys: ['institutionFrom', 'name'] },
        { name: t('File Name'), key: 'requiredAction', map:t },
        { name: t('Sent At'), key: 'recordTimestamp', map: dateTimeFormatter },
        { name: t('Data Removal Date'), key: 'dataRemovalTimestamp', map: dateTimeFormatter },
    ];

    const messageFields = [
        { name: t('Institution'), keys: ['institutionFrom', 'name'] },
        { name: t('Sent At'), key: 'IssueDate', map: dateTimeFormatter },
        { name: t('Data Removal Date'), key: 'dataRemovalTimestamp', map: dateTimeFormatter },
    ];

    const tabConfigs = [
        {
            label: t('Licenses'),
            key: 'licenses',
            pathSuffix: 'licenses',
            component: 
                <AccordionTable
                    contentList={process?.licenses}
                    contentFields={licensesFields}
                    accordionContentFields={licenseContentFields}
                    accordionContentsKey={'contents'}
                    displayAccordionTitle=''
                    displayAccordionTooltip=''
                />
        },
        { label: t('Transit Insitutions'), key: 'transitInstitutionList', pathSuffix: 'transit', fields: transitInstitutionFields},
        { label: t('Messages'), key: 'messages', pathSuffix: 'messages', fields: messageFields},
        { label: t('Attachments'), key: 'attachments', pathSuffix: 'attachments', fields: attachmentFields},
        { label: t('Status History'), key: 'processStatusHistory', pathSuffix: 'history', fields: statusHistoryFields},
        { label: t('Events'), key: 'events', pathSuffix: 'events', fields: eventsFields},
        { label: t('Assignments'), key: 'assignments', pathSuffix: 'assignments', fields: assignmentFields}
    ];
    
    const tabs = tabConfigs.map(({ label, key, fields, onClickAction, component }) => ({
        label,
        pathSuffix: key,
        component: () => {
            return component ? component : <ObjectTable
                list={process?.[key] ?? []}
                fields={fields}
                match={match}
                onClick={onClickAction}
            />
        },
    }));

    return <Paper>
        <Grid gap={3}>
            <Col size={12}>
                <PageTitle>Communication Transfer</PageTitle>
            </Col>

            <Col
                component={Paper}
                size='hidden' sm={12}
                className='lg:w-1/2 pb-8 pt-0.5 pl-3 pr-14 -mt-1'
                justify='center'
                style={{ width: '100%' }}
            >
                <TransferStepper transferStatus={process?.currentProcessStatus} />
            </Col>
            <Col size={12}>
                <ViewTable data={process} fields={processFields} />
            </Col>
            <Col size={12} className='mt-6'>
                <TabContainer match={match} tabs={tabs} />
            </Col>
        </Grid>
    </Paper>
};